import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Church, Feather, ScrollText, Music4, Landmark, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageIntro from "@/components/PageIntro";
import { Reveal, EASE } from "@/components/landing/Reveal";
import { scrollToId } from "@/lib/nav";
import AkathistAudio from "@/components/AkathistAudio";
import { PRAYER_EDITIONS } from "@/data/prayerEditions";
import { PublishedPrayer } from "@/components/prayers/PublishedPrayer";
import { PrayerAudio } from "@/components/prayers/PrayerAudio";
import { SUB_TUUM } from "@/data/historicalPrayers";

const CHAPTERS = [
  { id: "ephesus", n: "01", label: "Council of Ephesus", icon: Landmark },
  { id: "icons", n: "02", label: "Icon typology", icon: Feather },
  { id: "akathist", n: "03", label: "The Akathist", icon: ScrollText },
  { id: "dormition", n: "04", label: "Dormition & Assumption", icon: Church },
  { id: "troparia", n: "05", label: "Prayer editions", icon: Music4 },
  { id: "shrines", n: "06", label: "Eastern shrines", icon: Landmark },
  { id: "shared", n: "07", label: "Shared feasts", icon: Church },
];

const ICON_TYPES = [
  {
    key: "hodegetria",
    greek: "Ὁδηγήτρια",
    en: "Hodegetria",
    gloss: "She Who Shows the Way",
    text: "The Theotokos, upright and frontal, holds the Christ Child on her left arm and gestures to Him with her right hand. Her whole body is a signpost: not to herself, but to her Son, the Way and the Life.",
    ex: "Icon of Constantinople (traditionally attributed to St. Luke), Our Lady of Perpetual Help, the Salus Populi Romani.",
  },
  {
    key: "eleusa",
    greek: "Ἐλεοῦσα",
    en: "Eleousa",
    gloss: "The Tender-Merciful",
    text: "Mother and Child press cheek to cheek. Christ throws his arm around her neck; Mary's face carries all future sorrows. The Mother of God is tender towards God-with-us — a portrait of the Incarnation in one embrace.",
    ex: "Our Lady of Vladimir, the Kykkotissa, Our Lady of Kazan in the Eleousa manner, the Virgin of Tenderness.",
  },
  {
    key: "platytera",
    greek: "Πλατυτέρα τῶν οὐρανῶν",
    en: "Platytera",
    gloss: "More spacious than the heavens",
    text: "The Virgin, arms lifted in the Orans posture, holds the Christ Child in a circle at her breast. The Church calls her larger than the heavens because the One whom heaven cannot contain took flesh from her.",
    ex: "Apse mosaics of Kyiv's St. Sophia and Ravenna; the Panagia of the Sign; countless sanctuary apses in the East.",
  },
  {
    key: "panagia",
    greek: "Παναγία",
    en: "Panagia · All-Holy",
    gloss: "A liturgical title for every icon of the Mother",
    text: "Every Eastern icon of Mary — whatever the type — is addressed as Panagia, All-Holy. She is not called the Immaculate; she is called the All-Holy, honoured with the same word the East uses of God, but as pure gift.",
    ex: "The Panagia Portaitissa of Iviron; the Panagia Soumela; the Panagia Tricherousa (Three-Handed) of Hilandar.",
  },
];

const SHRINES = [
  {
    name: "Zarvanytsia",
    place: "Ukrainian Greek Catholic Church · Ternopil, Ukraine",
    since: "1240",
    text: "The Zarvanytsia icon, found by a monk fleeing the Mongol invasion, has been the greatest Marian pilgrimage of the Ukrainian Greek Catholic Church for eight centuries. Crowned by Pope St. John Paul II in 1996.",
  },
  {
    name: "Máriapócs",
    place: "Byzantine Catholic (Hungarian & Ruthenian) · Hungary",
    since: "1696",
    text: "The 'weeping icon' of the Mother of God wept in 1696, was brought to Vienna, and there a copy continues to weep to this day. Empress Maria Theresa built the basilica; John Paul II celebrated the Divine Liturgy here in 1991.",
  },
  {
    name: "Sayyidat Lubnan · Harissa",
    place: "Maronite Catholic Church · Lebanon",
    since: "1908",
    text: "The great white statue of Our Lady of Lebanon overlooks the bay of Jounieh — a sign of the Maronite faith through war, migration and diaspora. Kept in first-Sunday-of-May pilgrimage by all the Christians of Lebanon.",
  },
  {
    name: "Saidnaya",
    place: "Melkite Greek Catholic · Damascus, Syria",
    since: "547",
    text: "The Chagoura icon of the Theotokos, attributed to St. Luke and enshrined by Emperor Justinian, is venerated by Melkite Catholics, Antiochian Orthodox, and Muslims alike — a rare surviving witness of Marian unity in the Levant.",
  },
];

const SHARED_FEASTS = [
  { west: "Solemnity of Mary, Mother of God (1 January)", east: "Synaxis of the Theotokos (26 December) & Circumcision (1 January)" },
  { west: "Presentation of the Lord — Candlemas (2 February)", east: "The Meeting of the Lord · Hypapante (2 February)" },
  { west: "Annunciation of the Lord (25 March) — Solemnity", east: "Evangelismos · Great Feast (25 March)" },
  { west: "Assumption of the Blessed Virgin Mary (15 August)", east: "Dormition of the Theotokos · Great Feast (15 August)" },
  { west: "Nativity of the Blessed Virgin Mary (8 September)", east: "Nativity of the Theotokos · Great Feast (8 September)" },
  { west: "Presentation of Mary (21 November) — Memorial", east: "Entrance of the Theotokos into the Temple · Great Feast" },
  { west: "Immaculate Conception (8 December) — Solemnity", east: "Conception of the Theotokos by St. Anne (9 December)" },
  { west: "Our Lady of the Rosary (7 October)", east: "Protection of the Theotokos · Pokrov (1 October)" },
];

const Chapter = ({ id, n, title, kicker, children }) => (
  <section id={id} className="scroll-mt-28 border-t border-marian/15 py-14 first:border-t-0 first:pt-4" data-testid={`ew-${id}`}>
    <Reveal>
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Chapter {n}{kicker ? ` · ${kicker}` : ""}</p>
      <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-midnight sm:text-4xl">{title}</h2>
    </Reveal>
    <div className="mt-8 space-y-6 text-base leading-relaxed text-charcoal/90 sm:text-lg">{children}</div>
  </section>
);

export default function EastAndWestPage() {
  const { hash } = useLocation();
  const [active, setActive] = useState("ephesus");

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-30% 0px -60% 0px" }
    );
    CHAPTERS.forEach((c) => {
      const el = document.getElementById(c.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (hash) setTimeout(() => scrollToId(hash.slice(1)), 150);
  }, [hash]);

  return (
    <main data-testid="east-and-west-page">
      <Seo
        title="Theotokos in East and West — Akathist, Dormition, and Eastern shrines · Theotokos · Mater Dei"
        siteName="Theotokos · Mater Dei"
        description="One Mother confessed by two lungs of the Church. The Akathist Hymn, the icon typology, the Dormition and the Assumption, the great Eastern Catholic shrines, and a table of shared feasts of the Marian year."
      />
      <PageIntro
        crumbs={[{ label: "Home", to: "/" }, { label: "Discover", to: "/apologetics" }, { label: "East & West" }]}
        eyebrow="Discover · East and West"
        lines={["Theotokos in", "East and West"]}
        aside={
          <div className="rounded-2xl border border-marian/15 bg-white/70 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Two lungs, one Church</p>
            <p data-testid="east-west-jp2-quote" className="mt-2 font-serif text-lg leading-snug text-midnight">
              “the Church must breathe with her two lungs!”
            </p>
            <a href="https://www.vatican.va/content/john-paul-ii/en/encyclicals/documents/hf_jp-ii_enc_25051995_ut-unum-sint.html" target="_blank" rel="noreferrer" data-testid="east-west-jp2-source" className="mt-2 block text-xs text-marian underline underline-offset-4 transition-colors hover:text-midnight">St. John Paul II · Ut Unum Sint, §54 · excerpt</a>
          </div>
        }
      >
        The Latin Church and the twenty-three Eastern Catholic Churches confess one and the same
        Mother of God. This is her portrait as she is prayed on both sides of the great tradition
        — from the Council of Ephesus to the icons of Athos, from the Akathist Hymn to the Assumption.
      </PageIntro>

      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-14 sm:px-8 lg:grid-cols-[15rem_minmax(0,1fr)] lg:px-16">
        <nav aria-label="Chapters" className="hidden lg:block">
          <ol className="sticky top-32 space-y-1">
            {CHAPTERS.map((s) => {
              const Icon = s.icon;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    data-testid={`ew-toc-${s.id}`}
                    onClick={() => scrollToId(s.id)}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                      active === s.id ? "bg-paleblue text-midnight" : "text-slateblue hover:text-marian"
                    }`}
                  >
                    <span className="font-mono text-[10px] text-gold">{s.n}</span>
                    <Icon className="h-3.5 w-3.5" />
                    {s.label}
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="min-w-0 max-w-3xl">
          <Chapter id="ephesus" n="01" kicker="Ephesus, 431" title="How Mary became Theotokos">
            <p>
              In 431 the bishops of the universal Church gathered at Ephesus, in the very city where
              tradition places the sleeping of the Mother of God, and settled a fierce dispute over
              a single Greek word. Was Mary the mother of the man Jesus alone — Christotokos — or
              the Mother of God himself made flesh: <em>Theotokos</em>?
            </p>
            <p>
              The Council answered: Theotokos. Not because Mary is older than her Son, nor because
              she is the origin of his divinity, but because the One she carried in her womb, bore
              in Bethlehem, and stood beside at Calvary is a single divine Person with two natures.
              To confess Mary as Mother of God is to confess that in her womb heaven met earth.
            </p>
            <p>
              Every Marian feast, every icon, every stanza of the Akathist, every Ave and every
              Salaam ya Maryam of the Eastern Christians of the Arab world flows from that single
              conciliar word. It is the shared root of everything on the pages that follow.
            </p>
          </Chapter>

          <Chapter id="icons" n="02" kicker="Types of the Theotokos" title="Reading a Marian icon">
            <p>
              An icon is not decoration; it is theology in colour. The Eastern Church has never
              improvised her images of the Mother of God: three great types, together with a fourth
              liturgical title, contain almost every icon you will see from the catacombs to Athos.
            </p>
            <ul className="not-prose grid gap-4 sm:grid-cols-2" data-testid="icon-typology">
              {ICON_TYPES.map((it, i) => (
                <Reveal key={it.key} delay={i * 0.05}>
                  <li className="card-lift h-full rounded-2xl border border-marian/15 bg-white/90 p-6" data-testid={`icon-${it.key}`}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">Type {i + 1}</p>
                    <p className="mt-2 font-serif text-2xl font-semibold text-midnight">{it.en}</p>
                    <p className="mt-1 font-serif text-sm italic text-slateblue">{it.greek} — {it.gloss}</p>
                    <p className="mt-4 text-sm leading-relaxed text-charcoal/90">{it.text}</p>
                    <p className="mt-4 border-t border-marian/10 pt-3 text-xs italic leading-relaxed text-slateblue">
                      {it.ex}
                    </p>
                  </li>
                </Reveal>
              ))}
            </ul>
          </Chapter>

          <Chapter id="akathist" n="03" kicker="A Byzantine masterwork" title="The Akathist Hymn">
            <p>
              Sung standing (<em>a-kathistos</em>, “not seated”), the Akathist is the great poetic
              hymn of the Mother of God — twenty-four stanzas alternating between long meditations
              and short refrains, one for each letter of the Greek alphabet. Attributed by tradition
              to St. Romanos the Melodist in the sixth century, it is chanted whole on the Fifth
              Saturday of Great Lent and, in fragments, on every Friday of the fast.
            </p>
            <AkathistAudio />
            <div data-testid="akathist-panel" className="border-y border-marian/15 py-6"><PublishedPrayer prayer={PRAYER_EDITIONS.chairetismoi} prefix="eastern-akathist" /><p data-testid="akathist-edition-note" className="mt-4 text-xs text-slateblue">A complete named publisher edition replaces the former unsourced English adaptation. No translation is composed for this website.</p></div>
          </Chapter>

          <Chapter id="dormition" n="04" kicker="One mystery, two feasts" title="Dormition and Assumption">
            <p>
              What the East calls the <em>Koimesis</em> — the Falling-Asleep — the West calls the
              Assumption. The two names describe one mystery: at the end of her earthly life, the
              Mother of God was taken into the glory of her Son, body and soul. But the same feast
              is kept in two different keys.
            </p>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="rounded-2xl border border-marian/15 bg-white/90 p-6" data-testid="dormition-east">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">East · 15 August</p>
                <p className="mt-2 font-serif text-2xl font-semibold text-midnight">Dormition</p>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/90">
                  A fortnight of fasting; the Paraklesis to the Theotokos sung every evening. On the
                  eve, a bier bearing the icon of the Sleeping Virgin is processed through the church.
                  Christ, in the icon, holds her soul as a swaddled infant — the mother now becomes the
                  child.
                </p>
              </div>
              <div className="rounded-2xl border border-marian/15 bg-white/90 p-6" data-testid="dormition-west">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">West · 15 August</p>
                <p className="mt-2 font-serif text-2xl font-semibold text-midnight">Assumption</p>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/90">
                  Defined as dogma by Pius XII in <em>Munificentissimus Deus</em> (1950): the Immaculate
                  Mother of God, having completed the course of her earthly life, was taken up body and
                  soul into heavenly glory. Kept as a Solemnity of obligation across the Latin Church.
                </p>
              </div>
            </div>
            <p className="text-sm italic leading-relaxed text-slateblue">
              The Armenian Catholic Church keeps the feast on the Sunday nearest 15 August, with the
              blessing of the first grapes; the Coptic Catholic Church prepares for it with the fifteen-day
              Fast of the Virgin (1–16 Mesra).
            </p>
          </Chapter>

          <Chapter id="troparia" n="05" kicker="Marian prayer · named editions" title="Prayer with the Mother of God">
            <div data-testid="troparia" className="space-y-8">
              <div><h3 className="font-serif text-xl">The Paraklesis</h3><PublishedPrayer prayer={PRAYER_EDITIONS.paraklesis} prefix="eastern-paraklesis" /><PrayerAudio prayerId="paraklesis" prefix="eastern-paraklesis" /></div>
              <div><h3 className="font-serif text-xl">{SUB_TUUM.title}</h3><p data-testid="eastern-sub-tuum-text" className="mt-4 font-serif italic">{SUB_TUUM.text}</p><a href={SUB_TUUM.sourceUrl} target="_blank" rel="noreferrer" data-testid="eastern-sub-tuum-source" className="mt-3 inline-block text-xs underline">{SUB_TUUM.sourceLabel} · Latin recension in historical English translation</a><PrayerAudio prayerId="sub-tuum" prefix="eastern-sub-tuum" /></div>
            </div>
          </Chapter>

          <Chapter id="shrines" n="06" kicker="Great pilgrimages of the East" title="Eastern Catholic shrines">
            <p>
              Four shrines, four Eastern Catholic Churches, four centuries of pilgrimage. Each preserves
              a Marian tradition proper to its own Church and rite; each is in full communion with the
              Church of Rome.
            </p>
            <ul className="not-prose grid gap-4 sm:grid-cols-2" data-testid="eastern-shrines">
              {SHRINES.map((s, i) => (
                <Reveal key={s.name} delay={i * 0.05}>
                  <li className="card-lift h-full rounded-2xl border border-marian/15 bg-white/90 p-6" data-testid={`shrine-${s.name.toLowerCase().split(" ")[0]}`}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">Since {s.since}</p>
                    <p className="mt-2 font-serif text-2xl font-semibold text-midnight">{s.name}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slateblue">{s.place}</p>
                    <p className="mt-4 text-sm leading-relaxed text-charcoal/90">{s.text}</p>
                  </li>
                </Reveal>
              ))}
            </ul>
          </Chapter>

          <Chapter id="shared" n="07" kicker="One calendar in two keys" title="Shared feasts, side by side">
            <p>
              A quick reference: the great Marian feasts of the year as they appear in the Latin
              calendar and in the calendars of the Eastern Catholic Churches. The same mystery, sometimes
              on the same day, sometimes translated into a different rank or a nearby date.
            </p>
            <div className="overflow-hidden rounded-2xl border border-marian/15 bg-white/95 shadow-[0_20px_50px_-32px_rgba(16,42,67,0.4)]">
              <div className="gold-rule" />
              <table className="w-full border-collapse text-left" data-testid="shared-feasts-table">
                <thead>
                  <tr className="bg-paleblue/60">
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.28em] text-gold sm:px-6">West · Latin</th>
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.28em] text-gold sm:px-6">East · Byzantine et al.</th>
                  </tr>
                </thead>
                <tbody>
                  {SHARED_FEASTS.map((row, i) => (
                    <tr key={row.west} className={i % 2 ? "bg-paleblue/20" : ""}>
                      <td className="border-t border-marian/10 px-4 py-3.5 font-serif text-sm text-midnight sm:px-6 sm:text-base">{row.west}</td>
                      <td className="border-t border-marian/10 px-4 py-3.5 font-serif text-sm text-midnight sm:px-6 sm:text-base">{row.east}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/calendar"
                data-testid="ew-calendar-link"
                className="group inline-flex items-center gap-2 rounded-full bg-marian px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-midnight"
              >
                Open the Marian calendar
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/apparitions"
                data-testid="ew-apparitions-link"
                className="text-sm font-semibold text-marian hover:text-midnight"
              >
                Or explore the apparitions and devotions →
              </Link>
            </div>
          </Chapter>
        </div>
      </div>

      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.1, ease: EASE }}
        className="bg-midnight py-16 text-ivory sm:py-20"
      >
        <div className="mx-auto max-w-4xl px-6 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">Coda</p>
          <h2 className="mt-5 font-serif text-3xl italic leading-snug sm:text-4xl">
            Χαῖρε, Κεχαριτωμένη · Ave, gratia plena · Rejoice, full of grace.
          </h2>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.28em] text-ivory/50">
            Greek · Latin · English — one greeting of the angel, one Mother of God.
          </p>
        </div>
      </motion.section>
    </main>
  );
}
